<template>
  <div class="email-view" dir="rtl">
    <!-- Hero Section with Animated Background -->
    <section class="relative pt-32 pb-20 bg-[#faf6df]">
      <div class="container mx-auto px-4 relative">
        <div class="text-center">
          <h1 class="text-6xl font-bold text-[#0f4640] mb-8 animate-fade-in">إدارة البريد الإلكتروني</h1>
          <p class="text-xl text-[#0f4640] max-w-3xl mx-auto opacity-90 leading-relaxed animate-fade-in-delay">
            يمكنك مزامنة حساب بريدك الإلكتروني مع سلاسة لإرسال واستقبال رسائل البريد الإلكتروني. مع إمكانية إدارة حسابات متعددة للبريد الوارد والصادر في نظام موحد.
          </p>
        </div>
      </div>
    </section>

    <!-- Main Content with Enhanced Styling -->
    <section class="py-20 bg-gray-50">
      <div class="container mx-auto px-4">
        <!-- Overview with Floating Effect -->
        <div class="mb-20 transform hover:-translate-y-1 transition-all duration-300">
          <div class="bg-white rounded-3xl shadow-2xl p-8 mb-12 relative overflow-hidden">
            <div class="absolute top-0 left-0 w-64 h-64 bg-gradient-to-bl from-[#0f4640] to-transparent opacity-5 rounded-full transform -translate-x-32 -translate-y-32"></div>
            <img src="@/assets/Emails.png" alt="واجهة البريد الإلكتروني" class="w-full rounded-2xl mb-8 shadow-lg hover:shadow-xl transition-all duration-300">
            <div class="text-right relative">
              <h2 class="text-4xl font-bold text-[#0f4640] mb-6">نظرة عامة</h2>
              <p class="text-xl text-gray-600 leading-relaxed">
                يمكنك إدارة حسابات البريد الإلكتروني المتعددة للوارد والصادر في سلاسة. يجب أن يكون هناك حساب صادر افتراضي واحد على الأقل وحساب وارد افتراضي واحد.
              </p>
            </div>
          </div>
        </div>

        <!-- Setup Process with Interactive Cards -->
        <div class="mb-20">
          <h2 class="text-4xl font-bold text-[#0f4640] mb-12 text-right">خطوات الإعداد</h2>
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <!-- Account Setup Card -->
            <div class="group bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 p-8 transform hover:-translate-y-2">
              <div class="relative">
                <div class="absolute inset-0 bg-gradient-to-r from-[#0f4640] to-transparent opacity-0 group-hover:opacity-5 rounded-2xl transition-all duration-300"></div>
                <img src="@/assets/email-account-list.png" alt="إعداد الحساب" class="w-full rounded-2xl mb-8 shadow-lg">
              </div>
              <h3 class="text-2xl font-bold text-[#0f4640] mb-6">إعداد الحساب</h3>
              <ol class="space-y-4 text-right">
                <li class="flex items-center gap-3 justify-end p-3 bg-gray-50 rounded-lg hover:bg-[#faf6df] transition-colors">
                  <span>انتقل إلى قائمة حسابات البريد الإلكتروني</span>
                  <div class="w-8 h-8 bg-[#0f4640] text-[#faf6df] rounded-full flex items-center justify-center flex-shrink-0">١</div>
                </li>
                <li class="flex items-center gap-3 justify-end p-3 bg-gray-50 rounded-lg hover:bg-[#faf6df] transition-colors">
                  <span>أدخل عنوان البريد الإلكتروني مع النطاق</span>
                  <div class="w-8 h-8 bg-[#0f4640] text-[#faf6df] rounded-full flex items-center justify-center flex-shrink-0">٢</div>
                </li>
                <li class="flex items-center gap-3 justify-end p-3 bg-gray-50 rounded-lg hover:bg-[#faf6df] transition-colors">
                  <span>أدخل كلمة مرور حساب البريد الإلكتروني</span>
                  <div class="w-8 h-8 bg-[#0f4640] text-[#faf6df] rounded-full flex items-center justify-center flex-shrink-0">٣</div>
                </li>
                <li class="flex items-center gap-3 justify-end p-3 bg-gray-50 rounded-lg hover:bg-[#faf6df] transition-colors">
                  <span>احفظ وانتظر اكتمال المزامنة</span>
                  <div class="w-8 h-8 bg-[#0f4640] text-[#faf6df] rounded-full flex items-center justify-center flex-shrink-0">٤</div>
                </li>
              </ol>
            </div>

            <!-- Requirements Card -->
            <div class="group bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 p-8 transform hover:-translate-y-2">
              <div class="relative">
                <div class="absolute inset-0 bg-gradient-to-r from-[#0f4640] to-transparent opacity-0 group-hover:opacity-5 rounded-2xl transition-all duration-300"></div>
                <img src="@/assets/email-account-incoming.png" alt="المتطلبات الأساسية" class="w-full rounded-2xl mb-8 shadow-lg">
              </div>
              <h3 class="text-2xl font-bold text-[#0f4640] mb-6">المتطلبات الأساسية</h3>
              <ul class="space-y-4 text-right">
                <li class="flex items-center gap-3 justify-end p-3 bg-gray-50 rounded-lg hover:bg-[#faf6df] transition-colors">
                  <span class="text-gray-600">نطاق بريد إلكتروني</span>
                  <i class="fas fa-check-circle text-[#0f4640] flex-shrink-0"></i>
                </li>
                <li class="space-y-3">
                  <div class="flex items-center gap-3 justify-end p-3 bg-gray-50 rounded-lg hover:bg-[#faf6df] transition-colors">
                    <span class="text-gray-600">مزودي الخدمة المدعومين</span>
                    <i class="fas fa-server text-[#0f4640] flex-shrink-0"></i>
                  </div>
                  <ul class="space-y-2 text-lg pr-8">
                    <li class="flex items-center gap-2 justify-end hover:text-[#0f4640] transition-colors">
                      <span>Gmail</span>
                      <i class="fab fa-google flex-shrink-0"></i>
                    </li>
                    <li class="flex items-center gap-2 justify-end hover:text-[#0f4640] transition-colors">
                      <span>Outlook</span>
                      <i class="fab fa-microsoft flex-shrink-0"></i>
                    </li>
                    <li class="flex items-center gap-2 justify-end hover:text-[#0f4640] transition-colors">
                      <span>Yahoo</span>
                      <i class="fab fa-yahoo flex-shrink-0"></i>
                    </li>
                  </ul>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Advanced Features with Modern Cards -->
        <div class="mb-20">
          <h2 class="text-4xl font-bold text-[#0f4640] mb-12 text-right">الميزات المتقدمة</h2>
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <!-- Incoming Settings Card -->
            <div class="group bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 p-8 transform hover:-translate-y-2">
              <div class="relative">
                <div class="absolute inset-0 bg-gradient-to-r from-[#0f4640] to-transparent opacity-0 group-hover:opacity-5 rounded-2xl transition-all duration-300"></div>
                <img src="@/assets/email-account-incoming.png" alt="إعدادات البريد الوارد" class="w-full rounded-2xl mb-8 shadow-lg">
              </div>
              <h3 class="text-2xl font-bold text-[#0f4640] mb-6 text-right">إعدادات البريد الوارد</h3>
              <ul class="space-y-4 text-right">
                <li class="flex items-center gap-3 justify-end p-3 bg-gray-50 rounded-lg hover:bg-[#faf6df] transition-colors">
                  <span class="text-gray-600">استخدام IMAP للمزامنة المتقدمة</span>
                  <i class="fas fa-sync text-[#0f4640]"></i>
                </li>
                <li class="flex items-center gap-3 justify-end p-3 bg-gray-50 rounded-lg hover:bg-[#faf6df] transition-colors">
                  <span class="text-gray-600">دعم SSL للأمان المتقدم</span>
                  <i class="fas fa-shield-alt text-[#0f4640]"></i>
                </li>
                <li class="flex items-center gap-3 justify-end p-3 bg-gray-50 rounded-lg hover:bg-[#faf6df] transition-colors">
                  <span class="text-gray-600">إدارة حدود المرفقات</span>
                  <i class="fas fa-paperclip text-[#0f4640]"></i>
                </li>
                <li class="flex items-center gap-3 justify-end p-3 bg-gray-50 rounded-lg hover:bg-[#faf6df] transition-colors">
                  <span class="text-gray-600">خيارات المزامنة المخصصة</span>
                  <i class="fas fa-cog text-[#0f4640]"></i>
                </li>
              </ul>
            </div>

            <!-- Outgoing Settings Card -->
            <div class="group bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 p-8 transform hover:-translate-y-2">
              <div class="relative">
                <div class="absolute inset-0 bg-gradient-to-r from-[#0f4640] to-transparent opacity-0 group-hover:opacity-5 rounded-2xl transition-all duration-300"></div>
                <img src="@/assets/email-account-sending.png" alt="إعدادات البريد الصادر" class="w-full rounded-2xl mb-8 shadow-lg">
              </div>
              <h3 class="text-2xl font-bold text-[#0f4640] mb-6 text-right">إعدادات البريد الصادر</h3>
              <ul class="space-y-4 text-right">
                <li class="flex items-center gap-3 justify-end p-3 bg-gray-50 rounded-lg hover:bg-[#faf6df] transition-colors">
                  <span class="text-gray-600">إعداد خادم SMTP المتقدم</span>
                  <i class="fas fa-server text-[#0f4640]"></i>
                </li>
                <li class="flex items-center gap-3 justify-end p-3 bg-gray-50 rounded-lg hover:bg-[#faf6df] transition-colors">
                  <span class="text-gray-600">إضافة توقيع مخصص</span>
                  <i class="fas fa-signature text-[#0f4640]"></i>
                </li>
                <li class="flex items-center gap-3 justify-end p-3 bg-gray-50 rounded-lg hover:bg-[#faf6df] transition-colors">
                  <span class="text-gray-600">تتبع حالة البريد في الوقت الفعلي</span>
                  <i class="fas fa-chart-line text-[#0f4640]"></i>
                </li>
                <li class="flex items-center gap-3 justify-end p-3 bg-gray-50 rounded-lg hover:bg-[#faf6df] transition-colors">
                  <span class="text-gray-600">إعداد الردود التلقائية</span>
                  <i class="fas fa-reply-all text-[#0f4640]"></i>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Enhanced Call to Action -->
        <div class="text-center bg-white rounded-3xl shadow-xl p-12 relative overflow-hidden">
          <div class="absolute inset-0 bg-gradient-to-l from-[#0f4640] to-transparent opacity-5"></div>
          <h3 class="text-3xl font-bold text-[#0f4640] mb-6">ابدأ باستخدام نظام البريد الإلكتروني المتكامل</h3>
          <p class="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            استمتع بتجربة إدارة البريد الإلكتروني السلسة مع سلاسة. ابدأ الآن واكتشف كيف يمكننا تحسين تواصلك.
          </p>
           
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'EmailView'
}
</script>

<style scoped>
.email-view {
  min-height: 100vh;
  font-family: 'Cairo', sans-serif;
  text-align: right;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

.animate-fade-in {
  animation: fadeIn 0.6s ease-out forwards;
}

.animate-fade-in-delay {
  animation: fadeIn 0.6s ease-out 0.3s forwards;
  opacity: 0;
}

/* Smooth hover transitions */
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 300ms;
}

/* RTL specific adjustments */
.fa-arrow-left {
  transform: scaleX(-1);
}

/* Ensure proper text alignment in RTL */
.text-right {
  text-align: right;
}

.text-left {
  text-align: left;
}

/* Adjust spacing for RTL */
.mr-2 {
  margin-left: 0.5rem;
  margin-right: 0;
}

.ml-2 {
  margin-right: 0.5rem;
  margin-left: 0;
}

/* Adjust icon spacing in RTL */
.flex.items-center.gap-3 i,
.flex.items-center.gap-2 i {
  margin-left: 0;
  margin-right: 0.75rem;
}

/* Ensure proper padding for nested lists */
ul.pr-8 {
  padding-right: 2rem;
}

/* Fix number alignment in circles */
.w-8.h-8 {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Add these new styles */
.flex.items-center {
  flex-direction: row-reverse;
}

.flex.justify-end {
  flex-direction: row;
}

.grid {
  direction: rtl;
}

.space-y-4 > * {
  text-align: right;
}
</style> 